import { Trash2 } from 'lucide-react';
import { BottomSheet, Button, NumPad } from '../../components';
import type { Category } from '../../data/types';
import { cn } from '../../lib/cn';
import { formatCurrency } from '../../lib/currency';
import { resolveIcon } from '../../lib/icons';

type Props = {
  /** Null while closed; the category being edited when open. */
  category: Category | null;
  currency: string;
  onClose: () => void;
  /** Save the new monthly allocation. Pass 0 to clear it. */
  onSave: (id: string, monthly: number) => void;
};

/**
 * Focused NumPad sheet for setting a category's monthly Zero-Based
 * allocation. Opened by tapping a category in the Zero-Based viz on the
 * Plan tab. NumPad owns its own buffer; we commit on Done.
 */
export function CategoryAllocationSheet({
  category,
  currency,
  onClose,
  onSave,
}: Props) {
  const open = !!category;
  const Icon = category ? resolveIcon(category.icon) : null;
  const initial = category?.zeroBasedBudget ?? 0;

  const submit = (committed: number) => {
    if (!category) return;
    onSave(category.id, committed);
    onClose();
  };

  return (
    <BottomSheet open={open} onClose={onClose} title="Monthly allocation">
      {category && Icon ? (
        <>
          <div className="px-card-pad pb-3 pt-1 flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-button shrink-0 inline-flex items-center justify-center text-white shadow-sm"
              style={{ backgroundColor: category.color }}
            >
              <Icon size={18} strokeWidth={1.5} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[15px] font-semibold text-text-primary truncate">
                {category.name}
              </div>
              <div className="text-[12px] text-text-secondary truncate">
                Currently{' '}
                {formatCurrency(category.zeroBasedBudget ?? 0, { currency })}{' '}
                / month
              </div>
            </div>
          </div>

          {/* Re-mount NumPad on category change so its internal buffer
              reseeds from the new initialValue. */}
          <NumPad
            key={category.id}
            initialValue={initial}
            onDone={submit}
            doneLabel="Save"
            caption="Monthly allocation"
          />

          {(category.zeroBasedBudget ?? 0) > 0 ? (
            <div className="px-card-pad pb-card-pad pt-1">
              <Button
                variant="ghost"
                fullWidth
                leadingIcon={<Trash2 size={16} strokeWidth={1.5} />}
                onClick={() => submit(0)}
                className={cn('text-negative')}
              >
                Clear allocation
              </Button>
            </div>
          ) : null}
        </>
      ) : null}
    </BottomSheet>
  );
}
